import { useState, useCallback, useEffect, useRef } from "react";
import type { Product, Persona } from "@shared/schema";
import type { NarrativeOpportunity, NarrativeAngle } from "@/lib/campaign-draft";
import {
  type CampaignDraft,
  type WizardStepId,
  type FunnelStrategyData,
  type MediaMixConfig,
  type AdUnitSelection,
  type AdUnitPlan,
  type PlannerPersona,
  type CreativeStrategistPersona,
  type MediaAllocation,
  type CampaignPlanSummary,
  type CreativeBriefsState,
  type StalenessState,
  WIZARD_STEPS,
  createEmptyDraft,
  createEmptyCreativeBriefsState,
  markDownstreamStale,
  isStepComplete,
  canAdvanceToStep,
} from "@/lib/campaign-draft";
import { apiRequest, queryClient } from "@/lib/queryClient";

export interface UseCampaignWizardReturn {
  draft: CampaignDraft;
  currentStepIndex: number;
  currentStepDef: (typeof WIZARD_STEPS)[number];
  totalSteps: number;
  canGoBack: boolean;
  canGoForward: boolean;
  goBack: () => void;
  goForward: () => void;
  forceGoForward: () => void;
  goToStep: (stepId: WizardStepId) => void;
  setProduct: (product: Product) => void;
  setSelectedVariantName: (name: string | null) => void;
  setPersona: (persona: Persona) => void;
  setNarrativeOpportunity: (opp: NarrativeOpportunity) => void;
  setNarrativeAngles: (angles: NarrativeAngle[]) => void;
  setFunnelStrategy: (strategy: FunnelStrategyData | null) => void;
  setMediaMix: (mix: MediaMixConfig) => void;
  setAdUnitSelections: (selections: AdUnitSelection[]) => void;
  setAdUnitPlan: (plan: AdUnitPlan | null) => void;
  setPlannerPersona: (persona: PlannerPersona | null) => void;
  setCreativeStrategist: (strategist: CreativeStrategistPersona | null) => void;
  setMediaAllocation: (allocation: MediaAllocation | null) => void;
  setCampaignPlan: (plan: CampaignPlanSummary | null) => void;
  setCreativeBriefs: (state: CreativeBriefsState) => void;
  setCampaignName: (name: string) => void;
  isStepCompleted: (stepId: WizardStepId) => boolean;
  isStepStale: (stepId: WizardStepId) => boolean;
  clearStale: (stepId: WizardStepId) => void;
  resetDraft: () => void;
  completedStepCount: number;
  serverCampaignId: string | null;
  isLoading: boolean;
}

function parseDraftFromNotes(notes: string | null, campaignId: string): CampaignDraft | null {
  if (!notes) return null;
  try {
    const parsed = JSON.parse(notes);
    if (parsed && parsed.currentStep && parsed.id) {
      if (!parsed.creativeBriefs || typeof parsed.creativeBriefs !== "object" || !("queue" in parsed.creativeBriefs)) {
        parsed.creativeBriefs = createEmptyCreativeBriefsState();
      }
      if (!Array.isArray(parsed.adUnitSelections)) parsed.adUnitSelections = [];
      if (Array.isArray(parsed.funnelStrategy)) parsed.funnelStrategy = null;
      if (!("adUnitPlan" in parsed) || parsed.adUnitPlan === undefined) parsed.adUnitPlan = null;
      if (!("plannerPersona" in parsed) || parsed.plannerPersona === undefined) parsed.plannerPersona = null;
      if (!("creativeStrategist" in parsed) || parsed.creativeStrategist === undefined) parsed.creativeStrategist = null;
      if (!("mediaAllocation" in parsed) || parsed.mediaAllocation === undefined) parsed.mediaAllocation = null;
      if (!parsed.staleness) {
        parsed.staleness = {
          product: false, persona: false, narrativeOpportunity: false,
          narrativeAngles: false, funnelStrategy: false, mediaMix: false,
          adUnitSelections: false, campaignPlan: false, creativeBriefs: false,
        };
      }
      if (!Array.isArray(parsed.completedSteps)) parsed.completedSteps = [];
      parsed.id = campaignId;
      return parsed as CampaignDraft;
    }
    return null;
  } catch {
    return null;
  }
}

export function useCampaignWizard(
  brandId: string,
  campaignId?: string,
  navigate?: (path: string) => void,
): UseCampaignWizardReturn {
  const [draft, setDraft] = useState<CampaignDraft>(() => createEmptyDraft());
  const [serverCampaignId, setServerCampaignId] = useState<string | null>(campaignId || null);
  const [isLoading, setIsLoading] = useState(true);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMount = useRef(true);
  const isSaving = useRef(false);
  const latestDraftRef = useRef<CampaignDraft>(draft);
  latestDraftRef.current = draft;
  const serverIdRef = useRef<string | null>(serverCampaignId);
  serverIdRef.current = serverCampaignId;

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        if (campaignId) {
          const res = await fetch(`/api/campaigns/${campaignId}`, { credentials: "include" });
          if (res.ok) {
            const campaign = await res.json();
            const loadedDraft = parseDraftFromNotes(campaign.notes, campaignId);
            if (loadedDraft && !cancelled) {
              setDraft(loadedDraft);
              setServerCampaignId(campaignId);
            } else if (!cancelled) {
              const fresh = createEmptyDraft();
              fresh.id = campaignId;
              if (campaign.name && campaign.name !== "Untitled Campaign") {
                fresh.name = campaign.name;
              }
              setDraft(fresh);
              setServerCampaignId(campaignId);
            }
          }
        } else {
          const fresh = createEmptyDraft();
          const slug = `draft-${Date.now()}`;
          const res = await apiRequest("POST", `/api/brands/${brandId}/campaigns`, {
            name: "Untitled Campaign",
            slug,
            campaignType: "standard",
            status: "draft",
            notes: JSON.stringify(fresh),
          });
          const campaign = await res.json();
          if (!cancelled) {
            fresh.id = campaign.id;
            setDraft(fresh);
            setServerCampaignId(campaign.id);
            queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/campaigns`] });
            if (navigate) {
              navigate(`/brands/${brandId}/campaign-wizard/${campaign.id}`);
            }
          }
        }
      } catch (err) {
        console.error("[CampaignWizard] Failed to initialize draft:", err);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          isInitialMount.current = false;
        }
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, [brandId, campaignId]);

  const dirtyRef = useRef(false);

  const saveToServer = useCallback(async () => {
    const id = serverIdRef.current;
    if (!id) return;

    if (isSaving.current) {
      dirtyRef.current = true;
      return;
    }

    isSaving.current = true;
    dirtyRef.current = false;
    try {
      const currentDraft = latestDraftRef.current;
      const updateData: Record<string, any> = {
        notes: JSON.stringify(currentDraft),
        name: currentDraft.name && currentDraft.name.trim().length > 0
          ? currentDraft.name
          : "Untitled Campaign",
        personaId: currentDraft.persona?.id ?? null,
        productId: currentDraft.product?.id ?? null,
        objective: currentDraft.narrativeOpportunity?.title ?? null,
      };
      await fetch(`/api/campaigns/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updateData),
      });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/campaigns`] });
    } catch (err) {
      console.error("[CampaignWizard] Auto-save failed:", err);
    } finally {
      isSaving.current = false;
      if (dirtyRef.current) {
        dirtyRef.current = false;
        saveToServer();
      }
    }
  }, []);

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveToServer();
    }, 800);
  }, [saveToServer]);

  useEffect(() => {
    if (isInitialMount.current) return;
    scheduleSave();
  }, [draft, scheduleSave]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (serverIdRef.current && !isInitialMount.current) {
        dirtyRef.current = false;
        const id = serverIdRef.current;
        const currentDraft = latestDraftRef.current;
        const updateData: Record<string, any> = {
          notes: JSON.stringify(currentDraft),
        };
        if (currentDraft.name && currentDraft.name !== "Untitled Campaign") {
          updateData.name = currentDraft.name;
        }
        fetch(`/api/campaigns/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(updateData),
          keepalive: true,
        }).catch(() => {});
      }
    };
  }, []);

  const updateDraft = useCallback(
    (updater: (prev: CampaignDraft) => Partial<CampaignDraft>) => {
      setDraft((prev) => {
        const updates = updater(prev);
        return { ...prev, ...updates };
      });
    },
    [],
  );

  const updateWithStaleness = useCallback(
    (stepId: WizardStepId, updater: (prev: CampaignDraft) => Partial<CampaignDraft>) => {
      setDraft((prev) => {
        const updates = updater(prev);
        const newStaleness = markDownstreamStale(prev.staleness, stepId);
        const clearedStaleness: StalenessState = { ...newStaleness, [stepId]: false };
        const newCompletedSteps = prev.completedSteps.includes(stepId)
          ? prev.completedSteps
          : [...prev.completedSteps, stepId];
        return { ...prev, ...updates, staleness: clearedStaleness, completedSteps: newCompletedSteps };
      });
    },
    [],
  );

  const currentStepIndex = WIZARD_STEPS.findIndex((s) => s.id === draft.currentStep);
  const currentStepDef = WIZARD_STEPS[currentStepIndex] ?? WIZARD_STEPS[0];
  const totalSteps = WIZARD_STEPS.length;

  const canGoBack = currentStepIndex > 0;
  const canGoForward =
    currentStepIndex < totalSteps - 1 &&
    isStepComplete(draft, draft.currentStep);

  const goBack = useCallback(() => {
    setDraft((prev) => {
      const idx = WIZARD_STEPS.findIndex((s) => s.id === prev.currentStep);
      if (idx <= 0) return prev;
      return { ...prev, currentStep: WIZARD_STEPS[idx - 1].id };
    });
  }, []);

  const goForward = useCallback(() => {
    setDraft((prev) => {
      const idx = WIZARD_STEPS.findIndex((s) => s.id === prev.currentStep);
      if (idx >= WIZARD_STEPS.length - 1) return prev;
      if (!isStepComplete(prev, prev.currentStep)) return prev;
      // Clicking Next is an explicit acknowledgment of the current step,
      // so clear its stale flag as we advance.
      const leavingStep = prev.currentStep;
      return {
        ...prev,
        currentStep: WIZARD_STEPS[idx + 1].id,
        staleness: { ...prev.staleness, [leavingStep]: false },
      };
    });
  }, []);

  const forceGoForward = useCallback(() => {
    setDraft((prev) => {
      const idx = WIZARD_STEPS.findIndex((s) => s.id === prev.currentStep);
      if (idx >= WIZARD_STEPS.length - 1) return prev;
      return { ...prev, currentStep: WIZARD_STEPS[idx + 1].id };
    });
  }, []);

  const goToStep = useCallback((stepId: WizardStepId) => {
    setDraft((prev) => {
      if (!canAdvanceToStep(prev, stepId)) return prev;
      return { ...prev, currentStep: stepId };
    });
  }, []);

  const setProduct = useCallback(
    (product: Product) => {
      // Task #744 — changing the product clears any prior variant choice.
      updateWithStaleness("product", () => ({ product, selectedVariantName: null }));
    },
    [updateWithStaleness],
  );

  const setSelectedVariantName = useCallback(
    (name: string | null) => {
      setDraft((prev) => ({ ...prev, selectedVariantName: name }));
    },
    [],
  );

  const setPersona = useCallback(
    (persona: Persona) => {
      updateWithStaleness("persona", () => ({ persona }));
    },
    [updateWithStaleness],
  );

  const setNarrativeOpportunity = useCallback(
    (opp: NarrativeOpportunity) => {
      updateWithStaleness("narrativeOpportunity", () => ({
        narrativeOpportunity: opp,
        narrativeAngles: [],
      }));
    },
    [updateWithStaleness],
  );

  const setNarrativeAngles = useCallback(
    (angles: NarrativeAngle[]) => {
      updateWithStaleness("narrativeAngles", () => ({ narrativeAngles: angles }));
    },
    [updateWithStaleness],
  );

  const setFunnelStrategy = useCallback(
    (strategy: FunnelStrategyData | null) => {
      updateWithStaleness("funnelStrategy", () => ({ funnelStrategy: strategy }));
    },
    [updateWithStaleness],
  );

  const setMediaMix = useCallback(
    (mix: MediaMixConfig) => {
      // Media mix is now folded into the Ad Unit Selection step, so changes
      // to the mix should mark that step's downstream (campaign plan, briefs)
      // as stale rather than treating media mix as its own wizard step.
      setDraft((prev) => {
        // If the static-unit total actually changed, the previously
        // generated per-stage media allocation bakes in the OLD count and
        // would mislead downstream displays (e.g. "44 statics planned" on
        // Creative Briefs when only 5 are required). Drop it so it gets
        // regenerated against the new count.
        const totalChanged =
          prev.mediaMix?.totalUnits !== mix.totalUnits ||
          prev.mediaMix?.staticUnits !== mix.staticUnits;
        const newStaleness = markDownstreamStale(prev.staleness, "mediaMix");
        return {
          ...prev,
          mediaMix: mix,
          mediaAllocation: totalChanged ? null : prev.mediaAllocation,
          staleness: newStaleness,
        };
      });
    },
    [],
  );

  const setAdUnitSelections = useCallback(
    (selections: AdUnitSelection[]) => {
      updateWithStaleness("adUnitSelections", () => ({ adUnitSelections: selections }));
    },
    [updateWithStaleness],
  );

  const setAdUnitPlan = useCallback(
    (plan: AdUnitPlan | null) => {
      updateWithStaleness("adUnitSelections", () => ({ adUnitPlan: plan }));
    },
    [updateWithStaleness],
  );

  const setPlannerPersona = useCallback(
    (persona: PlannerPersona | null) => {
      updateDraft(() => ({ plannerPersona: persona }));
    },
    [updateDraft],
  );

  const setCreativeStrategist = useCallback(
    (strategist: CreativeStrategistPersona | null) => {
      updateDraft(() => ({ creativeStrategist: strategist }));
    },
    [updateDraft],
  );

  const setMediaAllocation = useCallback(
    (allocation: MediaAllocation | null) => {
      setDraft((prev) => {
        const newStaleness = markDownstreamStale(prev.staleness, "campaignPlan");
        return { ...prev, mediaAllocation: allocation, staleness: { ...newStaleness, campaignPlan: false } };
      });
    },
    [],
  );

  const setCampaignPlan = useCallback(
    (plan: CampaignPlanSummary | null) => {
      updateWithStaleness("campaignPlan", (prev) => {
        const updates: Partial<CampaignDraft> = { campaignPlan: plan };
        if (plan && (!prev.name || prev.name === "Untitled Campaign") && plan.name && plan.name !== "Untitled Campaign") {
          updates.name = plan.name;
        }
        return updates;
      });
    },
    [updateWithStaleness],
  );

  const setCreativeBriefs = useCallback(
    (state: CreativeBriefsState) => {
      updateWithStaleness("creativeBriefs", () => ({ creativeBriefs: state }));
    },
    [updateWithStaleness],
  );

  const setCampaignName = useCallback(
    (name: string) => {
      updateDraft(() => ({ name }));
    },
    [updateDraft],
  );

  const isStepCompleted = useCallback(
    (stepId: WizardStepId) => isStepComplete(draft, stepId),
    [draft],
  );

  const isStepStale = useCallback(
    (stepId: WizardStepId) => draft.staleness[stepId],
    [draft.staleness],
  );

  const clearStale = useCallback((stepId: WizardStepId) => {
    setDraft((prev) => ({
      ...prev,
      staleness: { ...prev.staleness, [stepId]: false },
    }));
  }, []);

  const resetDraft = useCallback(() => {
    const fresh = createEmptyDraft();
    if (serverIdRef.current) {
      fresh.id = serverIdRef.current;
    }
    setDraft(fresh);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setTimeout(() => saveToServer(), 0);
  }, [saveToServer]);

  const completedStepCount = WIZARD_STEPS.filter((s) => isStepComplete(draft, s.id)).length;

  return {
    draft,
    currentStepIndex,
    currentStepDef,
    totalSteps,
    canGoBack,
    canGoForward,
    goBack,
    goForward,
    forceGoForward,
    goToStep,
    setProduct,
    setSelectedVariantName,
    setPersona,
    setNarrativeOpportunity,
    setNarrativeAngles,
    setFunnelStrategy,
    setMediaMix,
    setAdUnitSelections,
    setAdUnitPlan,
    setPlannerPersona,
    setCreativeStrategist,
    setMediaAllocation,
    setCampaignPlan,
    setCreativeBriefs,
    setCampaignName,
    isStepCompleted,
    isStepStale,
    clearStale,
    resetDraft,
    completedStepCount,
    serverCampaignId,
    isLoading,
  };
}
